import React, { useState } from 'react';
import { ArrowLeftIcon, CheckIcon, SaveIcon, SendIcon, ToggleLeftIcon, ToggleRightIcon, PlusIcon, Trash2Icon, AlertCircleIcon, ClockIcon } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { districts, deedTypes, landTypes, subRegistryOffices } from '../data/seedData';
type SectionId = 'primary' | 'deed' | 'area' | 'value' | 'parties' | 'poa' | 'ownership' | 'schedule' | 'periphery' | 'location' | 'mutation' | 'khajna' | 'transfer' | 'litigation';
interface Section {
  id: SectionId;
  label: string;
  required?: boolean;
}
const sections: Section[] = [{
  id: 'primary',
  label: 'Primary Info',
  required: true
}, {
  id: 'deed',
  label: 'Deed Details',
  required: true
}, {
  id: 'area',
  label: 'Area & Type',
  required: true
}, {
  id: 'value',
  label: 'Value & Costs'
}, {
  id: 'parties',
  label: 'Parties',
  required: true
}, {
  id: 'poa',
  label: 'Power of Attorney'
}, {
  id: 'ownership',
  label: 'Ownership Chain'
}, {
  id: 'schedule',
  label: 'Schedule of Land'
}, {
  id: 'periphery',
  label: 'Periphery'
}, {
  id: 'location',
  label: 'Live Location'
}, {
  id: 'mutation',
  label: 'Mutation'
}, {
  id: 'khajna',
  label: 'Khajna'
}, {
  id: 'transfer',
  label: 'Subsequent Transfer'
}, {
  id: 'litigation',
  label: 'Litigation'
}];
export function LandRecordCreate() {
  const {
    state,
    dispatch,
    navigate
  } = useApp();
  const {
    language,
    selectedRecordId,
    landRecords
  } = state;
  const isEditMode = state.currentView === 'land-record-edit' && selectedRecordId;
  const existingRecord = isEditMode ? landRecords.find(r => r.id === selectedRecordId) : null;
  const [activeSection, setActiveSection] = useState<SectionId>('primary');
  const [completedSections, setCompletedSections] = useState<Set<SectionId>>(new Set());
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  // Form state
  const [formData, setFormData] = useState({
    landName: existingRecord?.landName || '',
    status: existingRecord?.status || 'Active',
    verification: existingRecord?.verification || 'Pending',
    deedNo: existingRecord?.deedNo || '',
    deedType: existingRecord?.deedType || '',
    executionDate: existingRecord?.executionDate || '',
    registrationDate: existingRecord?.registrationDate || '',
    subRegistry: existingRecord?.subRegistry || '',
    mouza: existingRecord?.mouza || '',
    union: existingRecord?.union || '',
    upazila: existingRecord?.upazila || '',
    district: existingRecord?.district || '',
    areaDecimal: existingRecord?.areaDecimal?.toString() || '',
    landType: existingRecord?.landType || '',
    actualValue: existingRecord?.actualValue?.toString() || '',
    deedValue: existingRecord?.deedValue?.toString() || '',
    mouzaValue: existingRecord?.mouzaValue?.toString() || '',
    stampDuty: existingRecord?.registrationCosts?.stampDuty?.toString() || '',
    registrationFee: existingRecord?.registrationCosts?.registrationFee?.toString() || '',
    localGovtTax: existingRecord?.registrationCosts?.localGovtTax?.toString() || '',
    eStamp: existingRecord?.registrationCosts?.eStamp?.toString() || '',
    poaEnabled: existingRecord?.poa?.enabled || false,
    peripheryNorth: existingRecord?.periphery?.north || '',
    peripherySouth: existingRecord?.periphery?.south || '',
    peripheryEast: existingRecord?.periphery?.east || '',
    peripheryWest: existingRecord?.periphery?.west || '',
    locationLat: existingRecord?.location?.lat?.toString() || '',
    locationLng: existingRecord?.location?.lng?.toString() || '',
    locationLabel: existingRecord?.location?.label || ''
  });
  const [transferors, setTransferors] = useState(existingRecord?.transferors || [{
    name: '',
    nid: '',
    address: ''
  }]);
  const [transferees, setTransferees] = useState(existingRecord?.transferees || [{
    name: '',
    nid: '',
    address: '',
    representative: ''
  }]);
  const [ownershipChain, setOwnershipChain] = useState(existingRecord?.ownershipChain || []);
  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const calculateArea = (decimal: string) => {
    const dec = parseFloat(decimal) || 0;
    return {
      katha: (dec / 24.7).toFixed(2),
      acre: (dec / 66).toFixed(2)
    };
  };
  const calculateTotal = () => {
    const stamp = parseFloat(formData.stampDuty) || 0;
    const reg = parseFloat(formData.registrationFee) || 0;
    const tax = parseFloat(formData.localGovtTax) || 0;
    const eStamp = parseFloat(formData.eStamp) || 0;
    return stamp + reg + tax + eStamp;
  };
  const markSectionComplete = (sectionId: SectionId) => {
    setCompletedSections(prev => new Set([...prev, sectionId]));
    const currentIndex = sections.findIndex(s => s.id === sectionId);
    if (currentIndex < sections.length - 1) {
      setActiveSection(sections[currentIndex + 1].id);
    }
  };
  const handleSaveDraft = () => {
    setLastSaved(new Date().toLocaleTimeString());
    // In a real app, this would save to backend
  };
  const handleSubmit = () => {
    // In a real app, this would submit the form
    alert('Record saved successfully!');
    navigate('land-records');
  };
  const areaCalc = calculateArea(formData.areaDecimal);
  const renderSectionContent = () => {
    switch (activeSection) {
      case 'primary':
        return <div className="space-y-6" data-id="element-254">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-255">
              <div data-id="element-256">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-257">
                  Land ID{' '}
                  <span className="text-gray-400" data-id="element-258">(Auto-generated)</span>
                </label>
                <input type="text" value={isEditMode ? existingRecord?.landId : 'LR-2026-XXX'} disabled className="w-full px-4 py-2.5 bg-gray-100 border border-gray-300 rounded-lg text-gray-500" data-id="element-259" />
              </div>
              <div data-id="element-260">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-261">
                  Land Name <span className="text-red-500" data-id="element-262">*</span>
                </label>
                <input type="text" value={formData.landName} onChange={e => handleInputChange('landName', e.target.value)} placeholder="e.g., Factory Expansion – Dhamrai" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-263" />
              </div>
              <div data-id="element-264">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-265">
                  Status
                </label>
                <select value={formData.status} onChange={e => handleInputChange('status', e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-266">
                  <option value="Active" data-id="element-267">Active</option>
                  <option value="Inactive" data-id="element-268">Inactive</option>
                </select>
              </div>
              <div data-id="element-269">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-270">
                  Verification Status
                </label>
                <select value={formData.verification} onChange={e => handleInputChange('verification', e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-271">
                  <option value="Pending" data-id="element-272">Pending</option>
                  <option value="Verified" data-id="element-273">Verified</option>
                  <option value="Rejected" data-id="element-274">Rejected</option>
                </select>
              </div>
            </div>
          </div>;
      case 'deed':
        return <div className="space-y-6" data-id="element-275">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-276">
              <div data-id="element-277">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-278">
                  Deed No <span className="text-red-500" data-id="element-279">*</span>
                </label>
                <input type="text" value={formData.deedNo} onChange={e => handleInputChange('deedNo', e.target.value)} placeholder="e.g., DR-2026-01457" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono" data-id="element-280" />
              </div>
              <div data-id="element-281">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-282">
                  Deed Type
                </label>
                <select value={formData.deedType} onChange={e => handleInputChange('deedType', e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-283">
                  <option value="" data-id="element-284">Select type</option>
                  {deedTypes.map(d => <option key={d.id} value={d.name} data-id="element-285">
                      {d.name}
                    </option>)}
                </select>
              </div>
              <div data-id="element-286">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-287">
                  Execution Date
                </label>
                <input type="date" value={formData.executionDate} onChange={e => handleInputChange('executionDate', e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-288" />
              </div>
              <div data-id="element-289">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-290">
                  Registration Date
                </label>
                <input type="date" value={formData.registrationDate} onChange={e => handleInputChange('registrationDate', e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-291" />
              </div>
              <div data-id="element-292">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-293">
                  Sub-Registry Office
                </label>
                <select value={formData.subRegistry} onChange={e => handleInputChange('subRegistry', e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-294">
                  <option value="" data-id="element-295">Select office</option>
                  {subRegistryOffices.map(o => <option key={o.id} value={o.name} data-id="element-296">
                      {o.name}
                    </option>)}
                </select>
              </div>
              <div data-id="element-297">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-298">
                  District <span className="text-red-500" data-id="element-299">*</span>
                </label>
                <select value={formData.district} onChange={e => handleInputChange('district', e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-300">
                  <option value="" data-id="element-301">Select district</option>
                  {districts.map(d => <option key={d.id} value={d.name} data-id="element-302">
                      {d.name}
                    </option>)}
                </select>
              </div>
              <div data-id="element-303">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-304">
                  Upazila <span className="text-red-500" data-id="element-305">*</span>
                </label>
                <input type="text" value={formData.upazila} onChange={e => handleInputChange('upazila', e.target.value)} placeholder="Enter upazila" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-306" />
              </div>
              <div data-id="element-307">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-308">
                  Union/Ward
                </label>
                <input type="text" value={formData.union} onChange={e => handleInputChange('union', e.target.value)} placeholder="Enter union/ward" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-309" />
              </div>
              <div data-id="element-310">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-311">
                  Mouza <span className="text-red-500" data-id="element-312">*</span>
                </label>
                <input type="text" value={formData.mouza} onChange={e => handleInputChange('mouza', e.target.value)} placeholder="Enter mouza" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-313" />
              </div>
            </div>
          </div>;
      case 'area':
        return <div className="space-y-6" data-id="element-314">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-315">
              <div data-id="element-316">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-317">
                  Area (Decimal) <span className="text-red-500" data-id="element-318">*</span>
                </label>
                <input type="number" value={formData.areaDecimal} onChange={e => handleInputChange('areaDecimal', e.target.value)} placeholder="e.g., 150" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-319" />
              </div>
              <div data-id="element-320">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-321">
                  Area (Katha) <span className="text-gray-400" data-id="element-322">(Auto)</span>
                </label>
                <input type="text" value={areaCalc.katha} disabled className="w-full px-4 py-2.5 bg-gray-100 border border-gray-300 rounded-lg text-gray-500" data-id="element-323" />
              </div>
              <div data-id="element-324">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-325">
                  Area (Acre) <span className="text-gray-400" data-id="element-326">(Auto)</span>
                </label>
                <input type="text" value={areaCalc.acre} disabled className="w-full px-4 py-2.5 bg-gray-100 border border-gray-300 rounded-lg text-gray-500" data-id="element-327" />
              </div>
            </div>
            <div data-id="element-328">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-329">
                Land Type
              </label>
              <select value={formData.landType} onChange={e => handleInputChange('landType', e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-330">
                <option value="" data-id="element-331">Select type</option>
                {landTypes.map(l => <option key={l.id} value={l.name} data-id="element-332">
                    {l.name}
                  </option>)}
              </select>
            </div>
          </div>;
      case 'value':
        return <div className="space-y-6" data-id="element-333">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-334">
              <div data-id="element-335">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-336">
                  Actual Value (BDT)
                </label>
                <input type="number" value={formData.actualValue} onChange={e => handleInputChange('actualValue', e.target.value)} placeholder="e.g., 45000000" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-337" />
              </div>
              <div data-id="element-338">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-339">
                  Deed Value (BDT)
                </label>
                <input type="number" value={formData.deedValue} onChange={e => handleInputChange('deedValue', e.target.value)} placeholder="e.g., 38000000" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-340" />
              </div>
              <div data-id="element-341">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-342">
                  Mouza Value (BDT)
                </label>
                <input type="number" value={formData.mouzaValue} onChange={e => handleInputChange('mouzaValue', e.target.value)} placeholder="e.g., 35000000" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-343" />
              </div>
            </div>
            <div className="border-t border-gray-200 pt-6" data-id="element-344">
              <h4 className="font-medium text-gray-900 mb-4" data-id="element-345">
                Registration Costs
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-346">
                <div data-id="element-347">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-348">
                    Stamp Duty (BDT)
                  </label>
                  <input type="number" value={formData.stampDuty} onChange={e => handleInputChange('stampDuty', e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-349" />
                </div>
                <div data-id="element-350">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-351">
                    Registration Fee (BDT)
                  </label>
                  <input type="number" value={formData.registrationFee} onChange={e => handleInputChange('registrationFee', e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-352" />
                </div>
                <div data-id="element-353">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-354">
                    Local Govt Tax (BDT)
                  </label>
                  <input type="number" value={formData.localGovtTax} onChange={e => handleInputChange('localGovtTax', e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-355" />
                </div>
                <div data-id="element-356">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-357">
                    E-Stamp (BDT)
                  </label>
                  <input type="number" value={formData.eStamp} onChange={e => handleInputChange('eStamp', e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-358" />
                </div>
              </div>
              <div className="mt-4 p-4 bg-blue-50 rounded-lg" data-id="element-359">
                <div className="flex items-center justify-between" data-id="element-360">
                  <span className="font-medium text-gray-900" data-id="element-361">
                    Total Registration Cost
                  </span>
                  <span className="text-xl font-bold text-blue-600" data-id="element-362">
                    ৳ {calculateTotal().toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>;
      case 'parties':
        return <div className="space-y-6" data-id="element-363">
            <div data-id="element-364">
              <div className="flex items-center justify-between mb-4" data-id="element-365">
                <h4 className="font-medium text-gray-900" data-id="element-366">Transferor(s)</h4>
                <button onClick={() => setTransferors([...transferors, {
                name: '',
                nid: '',
                address: ''
              }])} className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700" data-id="element-367">
                  <PlusIcon className="w-4 h-4" data-id="element-368" /> Add Transferor
                </button>
              </div>
              <div className="space-y-4" data-id="element-369">
                {transferors.map((t, index) => <div key={index} className="p-4 bg-gray-50 rounded-lg" data-id="element-370">
                    <div className="flex justify-between mb-3" data-id="element-371">
                      <span className="text-sm font-medium text-gray-600" data-id="element-372">
                        Transferor {index + 1}
                      </span>
                      {transferors.length > 1 && <button onClick={() => setTransferors(transferors.filter((_, i) => i !== index))} className="text-red-500 hover:text-red-600" data-id="element-373">
                          <Trash2Icon className="w-4 h-4" data-id="element-374" />
                        </button>}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-375">
                      <input type="text" value={t.name} onChange={e => {
                    const updated = [...transferors];
                    updated[index].name = e.target.value;
                    setTransferors(updated);
                  }} placeholder="Name" className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-376" />
                      <input type="text" value={t.nid} onChange={e => {
                    const updated = [...transferors];
                    updated[index].nid = e.target.value;
                    setTransferors(updated);
                  }} placeholder="NID" className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono" data-id="element-377" />
                      <div className="md:col-span-2" data-id="element-378">
                        <input type="text" value={t.address} onChange={e => {
                      const updated = [...transferors];
                      updated[index].address = e.target.value;
                      setTransferors(updated);
                    }} placeholder="Address" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-379" />
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>

            <div className="border-t border-gray-200 pt-6" data-id="element-380">
              <div className="flex items-center justify-between mb-4" data-id="element-381">
                <h4 className="font-medium text-gray-900" data-id="element-382">Transferee(s)</h4>
                <button onClick={() => setTransferees([...transferees, {
                name: '',
                nid: '',
                address: '',
                representative: ''
              }])} className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700" data-id="element-383">
                  <PlusIcon className="w-4 h-4" data-id="element-384" /> Add Transferee
                </button>
              </div>
              <div className="space-y-4" data-id="element-385">
                {transferees.map((t, index) => <div key={index} className="p-4 bg-gray-50 rounded-lg" data-id="element-386">
                    <div className="flex justify-between mb-3" data-id="element-387">
                      <span className="text-sm font-medium text-gray-600" data-id="element-388">
                        Transferee {index + 1}
                      </span>
                      {transferees.length > 1 && <button onClick={() => setTransferees(transferees.filter((_, i) => i !== index))} className="text-red-500 hover:text-red-600" data-id="element-389">
                          <Trash2Icon className="w-4 h-4" data-id="element-390" />
                        </button>}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-391">
                      <input type="text" value={t.name} onChange={e => {
                    const updated = [...transferees];
                    updated[index].name = e.target.value;
                    setTransferees(updated);
                  }} placeholder="Name" className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-392" />
                      <input type="text" value={t.nid} onChange={e => {
                    const updated = [...transferees];
                    updated[index].nid = e.target.value;
                    setTransferees(updated);
                  }} placeholder="NID/TIN" className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono" data-id="element-393" />
                      <input type="text" value={t.representative || ''} onChange={e => {
                    const updated = [...transferees];
                    updated[index].representative = e.target.value;
                    setTransferees(updated);
                  }} placeholder="Representative (if company)" className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-394" />
                      <input type="text" value={t.address} onChange={e => {
                    const updated = [...transferees];
                    updated[index].address = e.target.value;
                    setTransferees(updated);
                  }} placeholder="Address" className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-395" />
                    </div>
                  </div>)}
              </div>
            </div>
          </div>;
      case 'poa':
        return <div className="space-y-6" data-id="element-396">
            <div className="flex items-center gap-4" data-id="element-397">
              <span className="text-sm font-medium text-gray-700" data-id="element-398">
                Power of Attorney?
              </span>
              <button onClick={() => handleInputChange('poaEnabled', !formData.poaEnabled)} className={`relative w-12 h-6 rounded-full transition-colors ${formData.poaEnabled ? 'bg-blue-600' : 'bg-gray-300'}`} data-id="element-399">
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${formData.poaEnabled ? 'left-7' : 'left-1'}`} data-id="element-400" />
              </button>
              <span className="text-sm text-gray-600" data-id="element-401">
                {formData.poaEnabled ? 'Yes' : 'No'}
              </span>
            </div>
            {formData.poaEnabled && <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4" data-id="element-402">
                <div data-id="element-403">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-404">
                    POA Deed No
                  </label>
                  <input type="text" placeholder="Enter POA deed number" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono" data-id="element-405" />
                </div>
                <div data-id="element-406">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-407">
                    Execution Date
                  </label>
                  <input type="date" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-408" />
                </div>
                <div data-id="element-409">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-410">
                    Principal
                  </label>
                  <input type="text" placeholder="Enter principal name" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-411" />
                </div>
                <div data-id="element-412">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-413">
                    Attorney
                  </label>
                  <input type="text" placeholder="Enter attorney name" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-414" />
                </div>
              </div>}
          </div>;
      case 'ownership':
        return <div className="space-y-4" data-id="element-415">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3" data-id="element-416">
              <AlertCircleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-417" />
              <div data-id="element-418">
                <p className="font-medium text-amber-800" data-id="element-419">
                  25-Year Chain Required
                </p>
                <p className="text-sm text-amber-700" data-id="element-420">
                  Complete ownership chain for the past 25 years is required for
                  full verification.
                </p>
              </div>
            </div>
            <div className="flex justify-end" data-id="element-421">
              <button onClick={() => setOwnershipChain([...ownershipChain, {
              id: Date.now().toString(),
              deedNo: '',
              deedType: '',
              registrationDate: '',
              transferor: '',
              transferee: '',
              history: ''
            }])} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm" data-id="element-422">
                <PlusIcon className="w-4 h-4" data-id="element-423" /> Add Entry
              </button>
            </div>
            {ownershipChain.length > 0 ? <div className="space-y-4" data-id="element-424">
                {ownershipChain.map((entry, index) => <div key={entry.id} className="p-4 border border-gray-200 rounded-lg" data-id="element-425">
                    <div className="flex justify-between mb-3" data-id="element-426">
                      <span className="text-sm font-medium text-gray-600" data-id="element-427">
                        Entry {index + 1}
                      </span>
                      <button onClick={() => setOwnershipChain(ownershipChain.filter((_, i) => i !== index))} className="text-red-500 hover:text-red-600" data-id="element-428">
                        <Trash2Icon className="w-4 h-4" data-id="element-429" />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-430">
                      <input type="text" placeholder="Deed No" className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono" data-id="element-431" />
                      <input type="text" placeholder="Deed Type" className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-432" />
                      <input type="date" className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-433" />
                      <input type="text" placeholder="Transferor" className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-434" />
                      <input type="text" placeholder="Transferee" className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-435" />
                      <input type="text" placeholder="History/Notes" className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-436" />
                    </div>
                  </div>)}
              </div> : <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-200 rounded-lg" data-id="element-437">
                <p data-id="element-438">No ownership chain entries yet</p>
                <p className="text-sm" data-id="element-439">
                  Click "Add Entry" to start building the chain
                </p>
              </div>}
          </div>;
      case 'periphery':
        return <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-440">
            <div data-id="element-441">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-442">
                North
              </label>
              <input type="text" value={formData.peripheryNorth} onChange={e => handleInputChange('peripheryNorth', e.target.value)} placeholder="e.g., Md. Rahim Mia's land" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-443" />
            </div>
            <div data-id="element-444">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-445">
                South
              </label>
              <input type="text" value={formData.peripherySouth} onChange={e => handleInputChange('peripherySouth', e.target.value)} placeholder="e.g., Public Road" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-446" />
            </div>
            <div data-id="element-447">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-448">
                East
              </label>
              <input type="text" value={formData.peripheryEast} onChange={e => handleInputChange('peripheryEast', e.target.value)} placeholder="e.g., Canal" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-449" />
            </div>
            <div data-id="element-450">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-451">
                West
              </label>
              <input type="text" value={formData.peripheryWest} onChange={e => handleInputChange('peripheryWest', e.target.value)} placeholder="e.g., Md. Jamal's land" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-452" />
            </div>
          </div>;
      case 'location':
        return <div className="space-y-6" data-id="element-453">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-454">
              <div data-id="element-455">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-456">
                  Latitude
                </label>
                <input type="text" value={formData.locationLat} onChange={e => handleInputChange('locationLat', e.target.value)} placeholder="e.g., 23.9103" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono" data-id="element-457" />
              </div>
              <div data-id="element-458">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-459">
                  Longitude
                </label>
                <input type="text" value={formData.locationLng} onChange={e => handleInputChange('locationLng', e.target.value)} placeholder="e.g., 90.3535" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono" data-id="element-460" />
              </div>
              <div data-id="element-461">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-462">
                  Location Label
                </label>
                <input type="text" value={formData.locationLabel} onChange={e => handleInputChange('locationLabel', e.target.value)} placeholder="e.g., Factory Site – Dhamrai" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-463" />
              </div>
            </div>
            <div className="bg-gray-100 rounded-lg h-64 flex items-center justify-center" data-id="element-464">
              <div className="text-center text-gray-500" data-id="element-465">
                <p className="font-medium" data-id="element-466">Map Preview</p>
                <p className="text-sm" data-id="element-467">Enter coordinates to see location</p>
              </div>
            </div>
          </div>;
      default:
        return <div className="text-center py-8 text-gray-500" data-id="element-468">
            <p data-id="element-469">Section content for {activeSection}</p>
            <p className="text-sm" data-id="element-470">Form fields would go here</p>
          </div>;
    }
  };
  return <div className="p-4 lg:p-6" data-id="element-471">
      {/* Header */}
      <div className="flex items-center justify-between mb-6" data-id="element-472">
        <div className="flex items-center gap-4" data-id="element-473">
          <button onClick={() => navigate('land-records')} className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-474">
            <ArrowLeftIcon className="w-5 h-5" data-id="element-475" />
          </button>
          <div data-id="element-476">
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900" data-id="element-477">
              {isEditMode ? 'Edit Land Record' : 'Create New Land Record'}
            </h1>
            {lastSaved && <p className="text-sm text-gray-500 flex items-center gap-1" data-id="element-478">
                <ClockIcon className="w-4 h-4" data-id="element-479" />
                Autosaved at {lastSaved}
              </p>}
          </div>
        </div>
        <div className="flex items-center gap-2" data-id="element-480">
          <button onClick={handleSaveDraft} className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-481">
            <SaveIcon className="w-4 h-4" data-id="element-482" />
            <span className="hidden sm:inline" data-id="element-483">Save Draft</span>
          </button>
          <button onClick={handleSubmit} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-484">
            <SendIcon className="w-4 h-4" data-id="element-485" />
            <span className="hidden sm:inline" data-id="element-486">Save & Submit</span>
          </button>
        </div>
      </div>

      <div className="flex gap-6" data-id="element-487">
        {/* Section Stepper - Desktop */}
        <div className="hidden lg:block w-64 flex-shrink-0" data-id="element-488">
          <div className="bg-white rounded-xl border border-gray-200 p-4 sticky top-6" data-id="element-489">
            <h3 className="font-medium text-gray-900 mb-4" data-id="element-490">Sections</h3>
            <div className="space-y-1" data-id="element-491">
              {sections.map((section, index) => {
              const isActive = activeSection === section.id;
              const isCompleted = completedSections.has(section.id);
              return <button key={section.id} onClick={() => setActiveSection(section.id)} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-492">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${isCompleted ? 'bg-green-500 text-white' : isActive ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'}`} data-id="element-493">
                      {isCompleted ? <CheckIcon className="w-4 h-4" data-id="element-494" /> : index + 1}
                    </span>
                    <span className="text-sm truncate" data-id="element-495">{section.label}</span>
                    {section.required && <span className="text-red-500 text-xs" data-id="element-496">*</span>}
                  </button>;
            })}
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="flex-1 min-w-0" data-id="element-497">
          {/* Mobile Section Selector */}
          <div className="lg:hidden mb-4" data-id="element-498">
            <select value={activeSection} onChange={e => setActiveSection(e.target.value as SectionId)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-499">
              {sections.map((section, index) => <option key={section.id} value={section.id} data-id="element-500">
                  {index + 1}. {section.label} {section.required ? '*' : ''}
                </option>)}
            </select>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6" data-id="element-501">
            <div className="flex items-center justify-between mb-6" data-id="element-502">
              <h2 className="text-lg font-semibold text-gray-900" data-id="element-503">
                {sections.find(s => s.id === activeSection)?.label}
              </h2>
              <button onClick={() => markSectionComplete(activeSection)} className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-504">
                Mark Complete & Next →
              </button>
            </div>
            {renderSectionContent()}
          </div>
        </div>
      </div>
    </div>;
}